import type { Ref } from 'vue'
import type { SearchEngine } from '~/utils/searchSurfaces'
import { resolveDefaultSearchEngine } from '~/utils/searchEngineForce'

export const SEARCH_ENGINE_STATE_KEY = 'search-engine'
export const SEARCH_ENGINE_CLIENT_STATE_KEY = 'search-engine-client'

/**
 * Writable shared engine state, hydration-safe: `SEARCH_ENGINE` on SSR and
 * during hydration. Only the searchEngine client plugin writes to it.
 */
export const useSearchEngineState = (): Ref<SearchEngine> => {
  const config = useRuntimeConfig()
  return useState<SearchEngine>(SEARCH_ENGINE_STATE_KEY, () =>
    resolveDefaultSearchEngine(config.public.searchEngine)
  )
}

/**
 * Engine the client resolved at boot (force cookie / `?searchEngine=`), set
 * before hydration. `null` on SSR. Only the searchEngine client plugin writes it.
 */
export const useClientSearchEngineState = (): Ref<SearchEngine | null> =>
  useState<SearchEngine | null>(SEARCH_ENGINE_CLIENT_STATE_KEY, () => null)

interface UseSearchEngineOptions {
  /**
   * For data that is only ever fetched on the client (`server: false`, or a
   * cache key split by `import.meta.client`): read the client-resolved engine
   * right away instead of waiting for hydration, so a forced visitor never
   * makes a throwaway request to the other engine. Don't use it to branch
   * SSR-rendered markup — that must stay on the hydration-safe value.
   */
  clientOnly?: boolean
}

/**
 * The active search engine for every surface (KFT-4115). Starts as
 * `SEARCH_ENGINE` on SSR and hydration; the client plugin may then switch it
 * (A/B force cookie, `?searchEngine=` QA override, or a live `force()` call),
 * so consumers must treat it as reactive and refetch when it changes.
 */
export const useSearchEngine = ({
  clientOnly = false,
}: UseSearchEngineOptions = {}): Readonly<Ref<SearchEngine>> => {
  const engine = useSearchEngineState()
  if (!clientOnly) return readonly(engine)

  const clientEngine = useClientSearchEngineState()
  return computed(() => clientEngine.value ?? engine.value)
}
